// Eruda mobile debug console — attivato via ?debug=1 o localStorage.eruda='1'
if (
  typeof window !== 'undefined' &&
  (location.search.includes('debug=1') || localStorage.getItem('eruda') === '1')
) {
  localStorage.setItem('eruda', '1');
  import('eruda').then(({ default: eruda }) => eruda.init());
}

import React from 'react';
import { createRoot } from 'react-dom/client';
import './global.css';
import App from './App';

declare global {
  interface Window {
    __deferredInstallPrompt?: Event & {
      prompt: () => Promise<void>;
      userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
    };
  }
}

/** In dev, SW/cache PWA di sessioni precedenti servono bundle stale e rompono HMR. */
async function clearDevPwaInterceptors(): Promise<void> {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if ('caches' in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((key) => caches.delete(key)));
  }
}

async function bootstrap(): Promise<void> {
  await clearDevPwaInterceptors();

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      (window as Window).__deferredInstallPrompt = e as Window['__deferredInstallPrompt'];
    });
  }

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element #root not found');
  }

  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );

  if (process.env.NODE_ENV === 'production') {
    window.setTimeout(() => {
      void Promise.all([
        import('./serviceWorkerRegistration'),
        import('./services/pwa-events'),
      ]).then(([sw, pwa]) => {
        sw.register({
          onSuccess: () => pwa.dispatchPWAEvent('offline-ready'),
          onUpdate: (registration) => {
            pwa.dispatchPWAEvent('update-available', registration);
          },
        });
      });
    }, 0);
  }

  window.setTimeout(() => {
    void import('./reportWebVitals').then(({ default: reportWebVitals }) => {
      reportWebVitals();
    });
  }, 0);

  window.setTimeout(() => {
    void import('./services/offline/health/quota-monitor').then(({ isPersisted, requestPersistence }) => {
      isPersisted()
        .then((alreadyPersisted) => {
          if (!alreadyPersisted) {
            requestPersistence().catch(() => {});
          }
        })
        .catch(() => {});
    });
  }, 0);

  window.setTimeout(() => {
    void import('./utils/authBroadcastChannel');
  }, 0);
}

void bootstrap();
